import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { Spinner } from '../ui/spinner';
import { intlLocale } from '../../Utils/locale';
import { useCurrency } from '../../contexts/Currency';

function UpcomingDividends() {
  const { t, i18n } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [dividends, setDividends] = useState([]);
  const [totalExpected, setTotalExpected] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUpcomingDividends();
  }, []);

  const loadUpcomingDividends = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/upcoming-dividends');

      setDividends(response.data.upcomingDividends || []);
      setTotalExpected(response.data.totalExpected || 0);

    } catch (err) {
      console.error('❌ Erro ao carregar próximos dividendos:', err);
      console.error('Detalhes do erro:', err.response?.data);
      setError(err.response?.data?.message || err.response?.data?.error || t('errorLoadingDividends'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(intlLocale(i18n.language), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
        <Spinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-red-200">
        <div className="text-center">
          <p className="text-red-600 font-medium">❌ {error}</p>
          <button
            onClick={loadUpcomingDividends}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            {t('tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (dividends.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
        <div className="text-center">
          <p className="text-4xl mb-4">📊</p>
          <p className="text-slate-600 font-medium">{t('noUpcomingDividends')}</p>
          <p className="text-sm text-slate-500 mt-2">{t('addDividendAssetsHint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              💰 {t('upcomingDividendsTitle')}
            </h2>
            <p className="text-sm text-slate-600 mt-1">{t('expectedDividendsSubtitle')}</p>
          </div>
          <button
            onClick={loadUpcomingDividends}
            className="px-4 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition text-sm font-medium"
            title={t('refresh')}
          >
            🔄
          </button>
        </div>
      </div>

      {/* Lista de Dividendos */}
      <div className="p-6 space-y-4">
        {dividends.map((asset, index) => (
          <div
            key={index}
            className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition"
          >
            {/* Header do Ativo */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-800">{asset.ticker}</h3>
                <p className="text-sm text-slate-600">{asset.assetName}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">{t('quantity')}</p>
                <p className="text-lg font-semibold text-blue-600">
                  {asset.totalQuantity.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Dividendos do Ativo */}
            <div className="space-y-2">
              {asset.dividends.map((dividend, divIndex) => (
                <div
                  key={divIndex}
                  className="bg-slate-50 rounded-lg p-3 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      📅 {formatDate(dividend.paymentDate)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatCurrency(dividend.rate)}/{t('perShare')} × {dividend.quantity}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrency(dividend.expectedAmount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer com Total */}
      <div className="p-6 border-t border-slate-200 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600">{t('totalExpected')}</p>
            <p className="text-xs text-slate-500 mt-1">
              {t('assetCount', { count: dividends.length })}
            </p>
          </div>
          <p className="text-3xl font-bold text-green-600">
            {formatCurrency(totalExpected)}
          </p>
        </div>
      </div>
    </div>
  );
}

export default UpcomingDividends;
